import httpClient from '@/lib/httpClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import useAuthStore from '@/store/useAuthStore'
import { useFeaturesLoaded, useHasFeature } from '@/components/feature-gate'
import {
  ACCOUNTING_PROVIDERS,
  type AccountingProvider,
} from '@/services/api/integrations/accountingProviders'

/** Query keys of the connection status, per provider. */
export const CONNECTION_KEYS: Record<AccountingProvider, string> = {
  xero: 'xero-connection',
  zoho: 'zoho-books-connection',
}

const base = (provider: AccountingProvider) =>
  `team/integrations/${ACCOUNTING_PROVIDERS[provider].slug}/connection`

/** Space the OAuth round trip was started from — the callback must post with the same X-Team. */
const connectTeamKey = (provider: AccountingProvider) =>
  `pf.${ACCOUNTING_PROVIDERS[provider].slug}.connect-team`

export interface AccountingConnection {
  id: number
  status: 'active' | 'paused' | 'error'
  needs_reconnect: boolean
  tenant_id: string
  tenant_name: string | null
  connected_by: { id: number; name: string; last_name: string | null } | null
  last_sync_at: string | null
  pause_reason: string | null
  paused_at: string | null
  /** Set while the provider's daily request limit is spent; syncing resumes after it. */
  rate_limited_until: string | null
  unmapped_bank_accounts: number
  invoices_pending_mapping: number
  needs_attention: boolean
}

export interface AccountingConnectionStatus {
  can_manage: boolean
  connection: AccountingConnection | null
}

export interface AccountingOrganisation {
  tenant_id: string
  tenant_name: string | null
}

export type AccountingCallbackResult =
  | {
      status: 'connected'
      connection: Pick<
        AccountingConnection,
        'id' | 'tenant_id' | 'tenant_name' | 'status'
      >
    }
  | {
      status: 'select_organisation'
      pending_id: string
      organisations: AccountingOrganisation[]
    }

export function useAccountingConnection(
  provider: AccountingProvider,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const teamId = useAuthStore((state) => state.currentTeamId)

  return useQuery<AccountingConnectionStatus>({
    queryKey: [CONNECTION_KEYS[provider], teamId],
    queryFn: async () => (await httpClient.get(base(provider))).data,
    enabled: enabled && !!teamId,
  })
}

/** Asks the API for the provider's consent URL and sends the browser there. */
export function useStartAccountingConnect(provider: AccountingProvider) {
  const teamId = useAuthStore((state) => state.currentTeamId)

  const mutation = useMutation({
    mutationFn: async () => {
      const response = await httpClient.post(`${base(provider)}/authorize`)
      return response.data.authorize_url as string
    },
    onSuccess: (url) => {
      try {
        sessionStorage.setItem(connectTeamKey(provider), String(teamId))
      } catch {
        // Storage unavailable — the callback falls back to the current Space.
      }
      window.location.assign(url)
    },
  })

  return { action: mutation.mutateAsync, status: mutation }
}

function connectTeamHeader(provider: AccountingProvider) {
  let teamId: string | null = null
  try {
    teamId = sessionStorage.getItem(connectTeamKey(provider))
  } catch {
    teamId = null
  }
  return teamId ? { 'X-Team': teamId } : {}
}

export function clearAccountingConnectTeam(provider: AccountingProvider) {
  try {
    sessionStorage.removeItem(connectTeamKey(provider))
  } catch {
    // ignore
  }
}

export function useCompleteAccountingConnect(provider: AccountingProvider) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      code,
      state,
      location,
    }: {
      code: string
      state: string
      /** Zoho only: the data centre of the account, as Zoho's callback names it. */
      location?: string | null
    }) => {
      const response = await httpClient.post(
        `${base(provider)}/callback`,
        { code, state, ...(location ? { location } : {}) },
        { headers: connectTeamHeader(provider) },
      )
      return response.data as AccountingCallbackResult
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [CONNECTION_KEYS[provider]] }),
  })

  return { action: mutation.mutateAsync, status: mutation }
}

export function useSelectAccountingOrganisation(provider: AccountingProvider) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      pendingId,
      tenantId,
    }: {
      pendingId: string
      tenantId: string
    }) => {
      const response = await httpClient.post(
        `${base(provider)}/organisation`,
        { pending_id: pendingId, tenant_id: tenantId },
        { headers: connectTeamHeader(provider) },
      )
      return response.data as AccountingCallbackResult
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [CONNECTION_KEYS[provider]] }),
  })

  return { action: mutation.mutateAsync, status: mutation }
}

export function useDisconnectAccounting(provider: AccountingProvider) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async () => (await httpClient.delete(base(provider))).data,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [CONNECTION_KEYS[provider]] }),
  })

  return { action: mutation.mutateAsync, status: mutation }
}

/** Resume a connection paused after repeated errors; the API syncs straight away. */
export function useResumeAccountingSync(provider: AccountingProvider) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async () =>
      (await httpClient.post(`${base(provider)}/resume`)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [CONNECTION_KEYS[provider]] }),
  })

  return { action: mutation.mutateAsync, status: mutation }
}

/**
 * The provider's connection status, fetched only once the plan catalogue has
 * loaded and includes the provider (a Space without the feature must not call
 * the gated endpoint).
 */
export function useGatedAccountingConnection(provider: AccountingProvider) {
  const hasFeature = useHasFeature(ACCOUNTING_PROVIDERS[provider].feature)
  const featuresLoaded = useFeaturesLoaded()

  return useAccountingConnection(provider, {
    enabled: featuresLoaded && hasFeature,
  })
}

/**
 * True when the Space's accounting connection needs someone to act (stopped,
 * or accounts/invoices waiting for mapping). Shares the status queries' cache.
 */
export function useAccountingNeedsAttention() {
  const xero = useGatedAccountingConnection('xero')
  const zoho = useGatedAccountingConnection('zoho')

  return (
    !!xero.data?.connection?.needs_attention ||
    !!zoho.data?.connection?.needs_attention
  )
}
